// Configuration for the <Dashboard> page.
//
// src/dashboard-config.json holds the built-in defaults: it is committed to the
// source repository and is what a browser sees before anything has been configured.
//
// Edits made in the browser are stored as the 'dashboardConfig' setting, which means
// the settings persister handles them like every other setting — cached in
// localStorage and, when "Store settings in sync backend" is on, written to
// /.organice-config.json in the sync backend and read back from there on startup.
// That is what makes the buttons survive a new device rather than one browser.

import defaultConfig from '../dashboard-config.json';

// The persisted settings field the configuration lives in. Also the localStorage key,
// which is why an older browser-local configuration is adopted rather than lost.
export const DASHBOARD_STORAGE_KEY = 'dashboardConfig';
export const DASHBOARD_BUTTON_COUNT = 8;
export const DASHBOARD_DEFAULT_ICON = 'file-alt';

const pad = (number) => `${number}`.padStart(2, '0');

// INFO: Deliberately not using `toISOString()`, which formats in UTC and
// therefore returns yesterday's date during the evening in +01:00/+02:00.
export const formatDatePath = (template, date = new Date()) =>
  `${template}`
    .replace(/YYYY/g, `${date.getFullYear()}`)
    .replace(/MM/g, pad(date.getMonth() + 1))
    .replace(/DD/g, pad(date.getDate()));

export const filePathToUrl = (path) => `/file${path.startsWith('/') ? path : `/${path}`}`;

/**
 * Whether a button points out of organice rather than at an Org file.
 *
 * INFO: Deliberately only http and https, not any 'scheme:' — a configuration field
 * that accepted arbitrary schemes would accept 'javascript:' too, and this value ends
 * up in an href.
 */
export const isExternalLink = (path) => /^https?:\/\//i.test(`${path || ''}`.trim());

// Verzeichnis eines Dateipfades: '/roam/daily/2026-07-28.org' -> '/roam/daily'
export const directoryOfPath = (filePath) => {
  const path = `${filePath || ''}`;
  const index = path.lastIndexOf('/');
  return index <= 0 ? '/' : path.slice(0, index);
};

// Die nächste tatsächlich vorhandene Datei in einem Verzeichnis, ausgehend von der
// gerade geöffneten. direction −1 = davor, +1 = danach.
//
// INFO: Bewusst über die vorhandenen Dateien statt über das Datum: Tagesnotizen
// entstehen nicht jeden Tag, und ein Sprung auf eine leere Datei wäre nur ein Umweg
// über eine Fehlermeldung. Sortiert wird nach dem Pfad — bei Namen der Form
// YYYY-MM-DD.org ist das genau die zeitliche Ordnung.
export const neighbourPath = (paths, currentPath, direction) => {
  const sorted = (Array.isArray(paths) ? paths : []).filter((p) => typeof p === 'string').sort();
  if (!sorted.length) return null;
  const index = sorted.indexOf(currentPath);
  if (index === -1) {
    // Die geöffnete Datei steht nicht in der Liste (etwa nach dem Umbenennen): dann von
    // ihrem Namen aus die nächstgelegene in der gewünschten Richtung nehmen.
    const kandidaten =
      direction < 0 ? sorted.filter((p) => p < currentPath) : sorted.filter((p) => p > currentPath);
    if (!kandidaten.length) return null;
    return direction < 0 ? kandidaten[kandidaten.length - 1] : kandidaten[0];
  }
  const ziel = index + (direction < 0 ? -1 : 1);
  return ziel >= 0 && ziel < sorted.length ? sorted[ziel] : null;
};

const stringOr = (value, fallback) => (typeof value === 'string' ? value : fallback);

const emptyButton = () => ({ label: '', path: '', icon: DASHBOARD_DEFAULT_ICON });

const normalizeButton = (button) => ({
  label: stringOr(button && button.label, '').trim(),
  path: stringOr(button && button.path, '').trim(),
  icon: stringOr(button && button.icon, '').trim() || DASHBOARD_DEFAULT_ICON,
});

// Always hand back exactly DASHBOARD_BUTTON_COUNT buttons, so the dashboard
// renders a stable grid no matter what the stored configuration looks like.
const normalizeButtons = (buttons) => {
  const normalized = (Array.isArray(buttons) ? buttons : [])
    .slice(0, DASHBOARD_BUTTON_COUNT)
    .map(normalizeButton);

  while (normalized.length < DASHBOARD_BUTTON_COUNT) {
    normalized.push(emptyButton());
  }

  return normalized;
};

export const normalizeDashboardConfig = (config) => ({
  version: defaultConfig.version,
  gtdPath: stringOr(config && config.gtdPath, defaultConfig.gtdPath).trim(),
  dailyPathTemplate: stringOr(
    config && config.dailyPathTemplate,
    defaultConfig.dailyPathTemplate
  ).trim(),
  buttons: normalizeButtons(config && config.buttons),
});

export const defaultDashboardConfig = () => normalizeDashboardConfig(defaultConfig);

/**
 * Read the configuration out of `state.base`.
 *
 * The value is an Immutable structure (every 'json' persisted field is), and may be
 * missing entirely on a fresh browser — normalizeDashboardConfig() then falls back to
 * src/dashboard-config.json, so callers always get a complete configuration.
 */
export const dashboardConfigFromState = (stored) =>
  normalizeDashboardConfig(
    stored && typeof stored.toJS === 'function' ? stored.toJS() : stored || defaultConfig
  );

// The exact contents to paste back into src/dashboard-config.json, so a
// configuration built in the browser can be committed to the repository.
export const serializeDashboardConfig = (config) =>
  `${JSON.stringify(normalizeDashboardConfig(config), null, 2)}\n`;
